import { red } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

import { ParagonColors } from '@paragon/client-tools';

declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      orange: string;
      red: string;
      purple: string;
      blue: string;
    };
    selectedList: {
      main: string;
    };
    canvas: {
      main: string;
    };
    border: {
      main: string;
    };
  }
  interface PaletteOptions {
    brand?: {
      orange: string;
      red: string;
      purple: string;
      blue: string;
    };
    selectedList?: {
      main: string;
    };
    canvas: {
      main: string;
    };
    border: {
      main: string;
    };
    appBar?: {
      main: string;
      progress: string;
      text: string;
      subtleText: string;
    };
  }
}

export const darkTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Paragon name
    h1: {
      fontSize: '1.6rem',
      fontFamily: 'Figtree',
      fontWeight: 400,
      letterSpacing: 1, //this works for the login, but the header needs 3
      textTransform: 'uppercase',
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    // MUI currently applies a semi-transparent gradient over dialogs causing the specified theme colors to be darker or lighter than intended.
    // This code removes that gradient, which they apply as a backgroundImage, so the intended theme colors are properly displayed.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#ffffff',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: ParagonColors.green,
          },
        },
        text: {
          backgroundColor: 'transparent',
          color: `#ffffff!important`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    brand: {
      orange: ParagonColors.orange,
      red: ParagonColors.red,
      purple: ParagonColors.purple,
      blue: ParagonColors.blue,
    },
    error: {
      main: red[900],
    },
    canvas: {
      main: ParagonColors.canvasDark,
    },
    selectedList: {
      main: 'rgba(77, 147, 120, 0.6)',
    },
    border: {
      main: '#c3c3c3',
    },
    background: {
      default: ParagonColors.background,
      paper: ParagonColors.background,
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    brand: {
      orange: ParagonColors.orange,
      red: ParagonColors.red,
      purple: ParagonColors.purple,
      blue: ParagonColors.blue,
    },
    error: {
      main: red[900],
    },
    canvas: {
      main: ParagonColors.canvasLight,
    },
    selectedList: {
      main: 'rgba(77, 147, 120, 0.6)',
    },
    border: {
      main: '#c3c3c3',
    },
  },
});

export const chTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.6rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    // MUI currently applies a semi-transparent gradient over dialogs causing the specified theme colors to be darker or lighter than intended.
    // This code removes that gradient, which they apply as a backgroundImage, so the intended theme colors are properly displayed.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#ee661b',
          color: '#ffffff',
          '&:hover': {
            borderColor: '#be5216',
          },
        },
        contained: {
          backgroundColor: '#ee661b',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#be5216',
            borderColor: '#be5216',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#6c757d',
    },
    secondary: {
      main: '#ee661b',
    },
    error: {
      main: red[900],
    },
    appBar: {
      main: '#6c757d',
      progress: '#ee661b',
      text: '#fff',
      subtleText: '#c0c0c0',
    },
    canvas: {
      main: ParagonColors.canvasDark,
    },
    selectedList: {
      main: 'rgba(108, 117, 125, 0.6)',
    },
    border: {
      main: '#c3c3c3',
    },
    background: {
      default: '#212529',
      paper: '#212529',
    },
  },
});
